import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Clock, AlertCircle, CheckCircle, FileText, FolderDown } from "lucide-react";

const API_BASE_URL = 'https://aaapd.org.et/gallery_backend/api';
const FILE_BASE_URL = API_BASE_URL.replace(/\/$/, '').replace(/\/api$/, '');

function formatDateTime(dt) {
  try {
    const d = typeof dt === 'string' ? new Date(dt) : dt;
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric', month: 'short', day: '2-igit',
      hour: '2-digit', minute: '2-digit'
    }).format(d);
  } catch {
    return dt || '';
  }
}

export default function AdminPlansReports() {
  const getFileUrl = (s) => {
    const id = s.id;
    if (id) return `${API_BASE_URL}/download_plan_report.php?id=${encodeURIComponent(id)}`;
    // Fallbacks if id not present
    const direct = s.file_url || s.fileUrl || s.url || '';
    if (direct) return direct;
    const name = s.filename || s.fileName || '';
    if (name) return `${FILE_BASE_URL}/uploads/plan_reports/${encodeURIComponent(name)}`;
    return '#';
  };

  const [loading, setLoading] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [showAnnualModal, setShowAnnualModal] = useState(false);
  const [annualDesc, setAnnualDesc] = useState("");
  const [annualFile, setAnnualFile] = useState(null);
  const [annualLoading, setAnnualLoading] = useState(false);
  const [annualYear, setAnnualYear] = useState('');
  const [activeTab, setActiveTab] = useState('submissions'); // 'submissions' | 'annual'
  const [annualReports, setAnnualReports] = useState([]);
  const [annualListLoading, setAnnualListLoading] = useState(false);
  const [annualEditingItem, setAnnualEditingItem] = useState(null);
  const [annualEditDesc, setAnnualEditDesc] = useState("");
  const [annualEditYear, setAnnualEditYear] = useState("");
  const [annualEditFile, setAnnualEditFile] = useState(null);
  const [annualActionLoading, setAnnualActionLoading] = useState(false);
  const [filterAnnualYear, setFilterAnnualYear] = useState("");
  const [annualDetailItem, setAnnualDetailItem] = useState(null);
  const availableAnnualYears = useMemo(() => {
    const ys = new Set();
    annualReports.forEach((r) => {
      const y = Number(r.year);
      if (Number.isFinite(y)) ys.add(y);
    });
    return Array.from(ys).sort((a,b) => b - a);
  }, [annualReports]);
  const filteredAnnualReports = useMemo(() => {
    if (!filterAnnualYear) return annualReports;
    const yy = Number(filterAnnualYear);
    return annualReports.filter((r) => Number(r.year) === yy);
  }, [annualReports, filterAnnualYear]);

  // Filters
  const [filterYear, setFilterYear] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterSubcity, setFilterSubcity] = useState("");
  const subcities = [
    "አዲስ ከተማ", "አቃቂ ቃሊቲ", "አራዳ", "ቦሌ", "ጉለሌ", 
    "ቂርቆስ", "ኮልፌ ቀራኒዮ", "ለሚ ኩራ", "ልደታ", "ንፋስ ስልክ ላፍቶ", "የካ"
  ];

  const availableYears = useMemo(() => {
    const ys = new Set();
    submissions.forEach((s) => {
      const y = Number(s.year || s.planYear);
      if (Number.isFinite(y)) ys.add(y);
    });
    return Array.from(ys).sort((a,b) => b - a);
  }, [submissions]);

  const filteredHistory = useMemo(() => {
    return submissions.filter((s) => {
      const y = Number(s.year || s.planYear);
      const t = (s.type || s.uploadType || '').toString().toLowerCase();
      const sc = (s.sub_city || s.subCity || '').toString().trim().toLowerCase();
      if (filterYear && (!Number.isFinite(y) || y !== Number(filterYear))) return false;
      if (filterType && t !== filterType.toLowerCase()) return false;
      if (filterSubcity && sc !== filterSubcity.toLowerCase()) return false;
      return true;
    });
  }, [submissions, filterYear, filterType, filterSubcity]);

  const fetchSubmissions = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE_URL}/get_all_plan_reports.php`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const list = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
      setSubmissions(list);
    } catch (e) {
      console.error('fetchSubmissions error', e);
      setError('Failed to load submissions');
      setSubmissions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const beginAnnualEdit = (item) => {
    setAnnualEditingItem(item);
    setAnnualEditDesc(item.description || "");
    setAnnualEditYear(item.year ? String(item.year) : "");
    setAnnualEditFile(null);
  };

  const cancelAnnualEdit = () => {
    setAnnualEditingItem(null);
    setAnnualEditDesc("");
    setAnnualEditYear("");
    setAnnualEditFile(null);
  };

  const submitAnnualEdit = async (e) => {
    e.preventDefault();
    if (!annualEditingItem) return;
    try {
      setAnnualActionLoading(true);
      const fd = new FormData();
      fd.append('id', String(annualEditingItem.id));
      fd.append('description', annualEditDesc || '');
      if (annualEditYear && /^\d{4}$/.test(annualEditYear)) fd.append('year', annualEditYear);
      if (annualEditFile) fd.append('file', annualEditFile);
      const res = await fetch(`${API_BASE_URL}/update_annual_report.php`, { method: 'POST', body: fd });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.status === 'error') throw new Error(data.message || `Update failed (HTTP ${res.status})`);
      setNotice('Annual report updated');
      cancelAnnualEdit();
      await fetchAnnualReports();
    } catch (e) {
      setError(e.message || 'Update failed');
    } finally {
      setAnnualActionLoading(false);
    }
  };

  const deleteAnnual = async (item) => {
    if (!item) return;
    if (!window.confirm('Delete this annual report? This cannot be undone.')) return;
    try {
      setAnnualActionLoading(true);
      const res = await fetch(`${API_BASE_URL}/delete_annual_report.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: item.id })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.status === 'error') throw new Error(data.message || `Delete failed (HTTP ${res.status})`);
      setNotice('Annual report deleted');
      await fetchAnnualReports();
    } catch (e) {
      setError(e.message || 'Delete failed');
    } finally {
      setAnnualActionLoading(false);
    }
  };

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  const fetchAnnualReports = useCallback(async () => {
    setAnnualListLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/get_annual_reports.php`);
      const data = await res.json().catch(() => ({}));
      const list = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
      setAnnualReports(list);
    } catch (e) {
      setAnnualReports([]);
    } finally {
      setAnnualListLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'annual') {
      fetchAnnualReports();
    }
  }, [activeTab, fetchAnnualReports]);

  const handleAnnualSubmit = async (e) => {
    e.preventDefault();
    if (!annualFile) return;
    try {
      setAnnualLoading(true);
      const fd = new FormData();
      fd.append('description', annualDesc || '');
      if (annualYear && /^\d{4}$/.test(annualYear)) {
        fd.append('year', annualYear);
      }
      fd.append('file', annualFile);
      const res = await fetch(`${API_BASE_URL}/upload_annual_report.php`, { method: 'POST', body: fd });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.status === 'error') {
        throw new Error(data.message || `Upload failed (HTTP ${res.status})`);
      }
      setShowAnnualModal(false);
      setAnnualDesc("");
      setAnnualFile(null);
      setAnnualYear('');
      setNotice('Annual report uploaded');
    } catch (e) {
      setError(e.message || 'Annual report upload failed');
    } finally {
      setAnnualLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-6xl mx-auto space-y-4">
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-5 py-6 text-white">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <FolderDown className="w-7 h-7" />
                <div>
                  <h1 className="text-2xl font-bold">ሁሉም እቅዶች እና ሪፖርቶች/All Plans & Reports</h1>
                  <p className="text-teal-100 text-sm">View all OPD submissions with filters by year and type.</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowAnnualModal(true)}
                className="px-4 py-2 rounded-lg bg-white text-teal-700 font-semibold shadow hover:bg-teal-50 hover:shadow-md transition"
              >
                ዓመታዊ ሪፖርት አስገባ/Submit Annual Report
              </button>
            </div>
          </div>
        </div>

        {(notice || error) && (
          <div className={`flex items-center gap-2 px-4 py-3 rounded-lg ${error ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'} border ${error ? 'border-red-200' : 'border-green-200'}`}>
            {error ? <AlertCircle className="w-5 h-5" /> : <CheckCircle className="w-5 h-5" />}
            <span className="text-sm">{error || notice}</span>
          </div>
        )}
        {/* Tabs */}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setActiveTab('submissions')}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${activeTab==='submissions' ? 'bg-teal-600 text-white' : 'bg-white text-teal-700 border border-teal-200 hover:bg-teal-50'}`}
          >
            Submissions
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('annual')}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${activeTab==='annual' ? 'bg-teal-600 text-white' : 'bg-white text-teal-700 border border-teal-200 hover:bg-teal-50'}`}
          >
            Annual History
          </button>
        </div>

        {activeTab === 'submissions' && (
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="w-5 h-5 text-gray-600" />
            <h3 className="text-base font-semibold text-gray-800">Submissions ({filteredHistory.length})</h3>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Year</label>
              <select
                value={filterYear}
                onChange={(e) => setFilterYear(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              >
                <option value="">ሁሉንም ዓመታት / All Years</option>
                {availableYears.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              >
                <option value="">ሁሉም ዓይነቶች / All Types</option>
                <option value="yearly">አመታዊ ፕላን / Plan (Yearly)</option>
                <option value="quarterly">ሪፖርት / Report (Quarterly)</option>
                <option value="annual">አመታዊ ሪፖርት / Annual Report</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Sub-city</label>
              <select
                value={filterSubcity}
                onChange={(e) => setFilterSubcity(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              >
                <option value="">ሁሉም ክፍለ ከተሞች</option>
                {subcities.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end gap-2">
              <button
                type="button"
                onClick={() => { setFilterYear(''); setFilterType(''); setFilterSubcity(''); }}
                className="px-3 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-800"
              >
                Reset
              </button>
              <div className="text-sm text-gray-600 ml-auto self-center">
                Showing {filteredHistory.length} of {submissions.length}
              </div>
            </div>
          </div>

          {loading && (
            <p className="text-sm text-gray-500">Loading...</p>
          )}

          {!loading && filteredHistory.length === 0 && (
            <p className="text-sm text-gray-500">No submissions found.</p>
          )}

          {!loading && filteredHistory.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-500 bg-gray-50">
                    <th className="px-3 py-2 w-12">#</th>
                    <th className="px-3 py-2">File</th>
                    <th className="px-3 py-2">ዓይነት/Type</th>
                    <th className="px-3 py-2">አመት/Year</th>
                    <th className="px-3 py-2">ሩብ ዓመት/Quarter</th>
                    <th className="px-3 py-2">ክፍለ ከተሞ/Sub-city</th>
                    <th className="px-3 py-2">የተላከበት ቀን / Submitted At</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 text-sm">
                  {filteredHistory
                    .slice()
                    .sort((a,b) => new Date(b.created_at || b.createdAt || 0) - new Date(a.created_at || a.createdAt || 0))
                    .map((s, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-gray-600">{idx + 1}</td>
                        <td className="px-3 py-2">
                          <a
                            href={getFileUrl(s)}
                            target="_blank"
                            rel="noopener noreferrer"
                            download
                            className="text-blue-600 hover:underline font-medium inline-flex items-center gap-1"
                            title="Download file"
                          >
                            <FileText className="w-4 h-4" /> {s.filename || s.fileName || 'file'}
                          </a>
                        </td>
                        <td className="px-3 py-2">
                          {(() => {
                            const tRaw = (s.type || s.uploadType || '').toString().toLowerCase();
                            const badgeClass = tRaw === 'yearly'
                              ? 'bg-teal-100 text-teal-700'
                              : tRaw === 'quarterly'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-purple-100 text-purple-700';
                            const label = tRaw === 'yearly'
                              ? 'YEARLY PLAN'
                              : tRaw === 'quarterly'
                                ? 'QUARTERLY REPORT'
                                : tRaw === 'annual'
                                  ? 'ANNUAL REPORT'
                                  : (s.type || s.uploadType || '').toString().toUpperCase() || '-';
                            return (
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${badgeClass}`}>
                                {label}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-3 py-2">{s.year || s.planYear || '-'}</td>
                        <td className="px-3 py-2">{s.quarter || s.planQuarter || '-'}</td>
                        <td className="px-3 py-2">{s.sub_city || s.subCity || '-'}</td>
                        <td className="px-3 py-2 text-gray-600">{formatDateTime(s.created_at || s.createdAt)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        )}

        {activeTab === 'annual' && (
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="w-5 h-5 text-gray-600" />
            <h3 className="text-base font-semibold text-gray-800">Annual Reports ({filteredAnnualReports.length})</h3>
          </div>
          {/* Year filter for annual reports */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">አመት/Year</label>
              <select
                value={filterAnnualYear}
                onChange={(e) => setFilterAnnualYear(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              >
                <option value="">ሁሉንም ዓመታት / All Years</option>
                {availableAnnualYears.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end gap-2">
              <button
                type="button"
                onClick={() => setFilterAnnualYear('')}
                className="px-3 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-800"
              >
                Reset
              </button>
              <div className="text-sm text-gray-600 ml-auto self-center">
                Showing {filteredAnnualReports.length} of {annualReports.length}
              </div>
            </div>
          </div>
          {annualListLoading && (
            <p className="text-sm text-gray-500">Loading...</p>
          )}
          {!annualListLoading && filteredAnnualReports.length === 0 && (
            <p className="text-sm text-gray-500">No annual reports found.</p>
          )}
          {!annualListLoading && filteredAnnualReports.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-500 bg-gray-50">
                    <th className="px-3 py-2 w-12">#</th>
                    <th className="px-3 py-2">ፋይል/File</th>
                    <th className="px-3 py-2">አመት / Year</th>
                    <th className="px-3 py-2">መግለጫ/Description</th>
                    <th className="px-3 py-2">የተላከበት ቀን / Uploaded At</th>
                    <th className="px-3 py-2">ድርጊቶች</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 text-sm">
                  {filteredAnnualReports.slice().sort((a,b) => new Date(b.created_at || b.createdAt || 0) - new Date(a.created_at || a.createdAt || 0)).map((r, idx) => (
                    <tr key={r.id || idx} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-600">{idx + 1}</td>
                      <td className="px-3 py-2 align-top">
                        <a
                          href={r?.id ? `${API_BASE_URL}/download_annual_report.php?id=${encodeURIComponent(r.id)}` : `${FILE_BASE_URL}/uploads/annual_reports/${encodeURIComponent(r.filename || r.fileName || '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                          className="text-blue-600 hover:underline font-medium"
                        >
                          {r.filename || r.fileName || 'file'}
                        </a>
                      </td>
                      <td className="px-3 py-2 align-top">{r.year || '-'}</td>
                      <td className="px-3 py-2 align-top whitespace-normal break-words text-gray-700">
                        {(() => {
                          const full = r.description || '';
                          const shown = full.length > 140 ? `${full.slice(0, 140)}…` : full || '-';
                          return (
                            <div className="flex items-start gap-2">
                              <span className="flex-1">{shown}</span>
                              {full && full.length > 140 && (
                                <button
                                  type="button"
                                  className="text-blue-600 hover:text-blue-800 text-xs whitespace-nowrap"
                                  onClick={() => setAnnualDetailItem(r)}
                                  title="View full description"
                                >
                                  View
                                </button>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-3 py-2 align-top text-gray-600">{formatDateTime(r.created_at || r.createdAt)}</td>
                      <td className="px-3 py-2 align-top">
                        <div className="flex items-center gap-2">
                          <button
                            className="px-2 py-1 text-gray-700 hover:text-gray-900 disabled:opacity-50"
                            onClick={() => setAnnualDetailItem(r)}
                            disabled={annualActionLoading}
                            title="Detail"
                          >
                            Detail
                          </button>
                          <button
                            className="px-2 py-1 text-blue-600 hover:text-blue-800 disabled:opacity-50"
                            onClick={() => beginAnnualEdit(r)}
                            disabled={annualActionLoading}
                            title="Edit"
                          >
                            Edit
                          </button>
                          <button
                            className="px-2 py-1 text-red-600 hover:text-red-800 disabled:opacity-50"
                            onClick={() => deleteAnnual(r)}
                            disabled={annualActionLoading}
                            title="Delete"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {/* Inline edit removed in favor of modal */}
        </div>
        )}
      </div>

      {showAnnualModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
          <div className="relative w-full max-w-lg bg-white rounded-xl shadow-2xl">
            <button
              type="button"
              onClick={() => setShowAnnualModal(false)}
              className="absolute top-3 right-3 p-2 rounded-full text-gray-500 hover:bg-gray-100"
              aria-label="Close"
              title="Close"
            >
              ×
            </button>
            <div className="p-5 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">Submit Annual Report</h3>
            </div>
            <form onSubmit={handleAnnualSubmit}>
              <div className="p-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Year (optional)</label>
                  <input
                    type="number"
                    min="2000"
                    max="2100"
                    value={annualYear}
                    onChange={(e) => setAnnualYear(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    placeholder={String(new Date().getFullYear())}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={annualDesc}
                    onChange={(e) => setAnnualDesc(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 min-h-[100px]"
                    placeholder="Write a short description..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">File</label>
                  <input
                    type="file"
                    onChange={(e) => setAnnualFile(e.target.files?.[0] || null)}
                    className="w-full text-sm"
                  />
                  {annualFile && (
                    <p className="text-xs text-gray-600 mt-1">Selected: <span className="font-medium">{annualFile.name}</span></p>
                  )}
                </div>
              </div>
              <div className="p-5 border-t border-gray-200 flex items-center justify-end gap-2">
                <button type="button" onClick={() => setShowAnnualModal(false)} className="px-4 py-2 rounded-lg bg-gray-200 text-gray-800 hover:bg-gray-300">Cancel</button>
                <button type="submit" className="px-4 py-2 rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50" disabled={!annualFile || annualLoading}>{annualLoading ? 'Submitting...' : 'Submit'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Annual Detail Modal */}
      {annualDetailItem && (
        <div className="fixed inset-0 z-[115] flex items-center justify-center bg-black/50 p-4">
          <div className="relative w-full max-w-lg bg-white rounded-xl shadow-2xl">
            <button
              type="button"
              onClick={() => setAnnualDetailItem(null)}
              className="absolute top-3 right-3 p-2 rounded-full text-gray-500 hover:bg-gray-100"
              aria-label="Close"
              title="Close"
            >
              ×
            </button>
            <div className="p-5 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">Annual Report Details</h3>
            </div>
            <div className="p-5 space-y-3 text-sm">
              <div className="grid grid-cols-3 gap-2">
                <div className="text-gray-500">File</div>
                <div className="col-span-2">
                  <a
                    href={annualDetailItem?.id ? `${API_BASE_URL}/download_annual_report.php?id=${encodeURIComponent(annualDetailItem.id)}` : `${FILE_BASE_URL}/uploads/annual_reports/${encodeURIComponent(annualDetailItem.filename || annualDetailItem.fileName || '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                    className="text-blue-600 hover:underline font-medium break-words"
                  >
                    {annualDetailItem.filename || annualDetailItem.fileName || 'file'}
                  </a>
                </div>
                <div className="text-gray-500">Year</div>
                <div className="col-span-2">{annualDetailItem.year || '-'}</div>
                <div className="text-gray-500">የተላከበት ቀን / Uploaded At</div>
                <div className="col-span-2">{formatDateTime(annualDetailItem.created_at || annualDetailItem.createdAt)}</div>
                <div className="text-gray-500">Description</div>
                <div className="col-span-2 whitespace-pre-wrap break-words">{annualDetailItem.description || '-'}</div>
              </div>
            </div>
            <div className="p-5 border-t border-gray-200 flex items-center justify-end">
              <button type="button" onClick={() => setAnnualDetailItem(null)} className="px-4 py-2 rounded-lg bg-gray-200 text-gray-800 hover:bg-gray-300">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Annual Edit Modal */}
      {annualEditingItem && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 p-4">
          <div className="relative w-full max-w-lg bg-white rounded-xl shadow-2xl">
            <button
              type="button"
              onClick={cancelAnnualEdit}
              className="absolute top-3 right-3 p-2 rounded-full text-gray-500 hover:bg-gray-100"
              aria-label="Close"
              title="Close"
            >
              ×
            </button>
            <div className="p-5 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">Edit Annual Report</h3>
            </div>
            <form onSubmit={submitAnnualEdit}>
              <div className="p-5 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                    <input
                      type="number"
                      min="2000"
                      max="2100"
                      value={annualEditYear}
                      onChange={(e) => setAnnualEditYear(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Replace File (optional)</label>
                    <input type="file" onChange={(e) => setAnnualEditFile(e.target.files?.[0] || null)} className="w-full text-sm" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={annualEditDesc}
                    onChange={(e) => setAnnualEditDesc(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 min-h-[100px]"
                  />
                </div>
              </div>
              <div className="p-5 border-t border-gray-200 flex items-center justify-end gap-2">
                <button type="button" onClick={cancelAnnualEdit} className="px-4 py-2 rounded-lg bg-gray-200 text-gray-800 hover:bg-gray-300">Cancel</button>
                <button type="submit" disabled={annualActionLoading} className="px-4 py-2 rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50">{annualActionLoading ? 'Saving...' : 'Save'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
